<script setup lang="ts">
import { computed, inject, type Ref } from 'vue';
import { Icon } from '@iconify/vue';
import { useRouter } from 'vue-router';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useCustomizerStore } from '@/shared/stores/customizer';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { usePreSetStore } from '@/features/startTask/preset_task/shared/preset/stores/preSetStore';
import { isPreSetSupported } from '@/features/startTask/preset_task/jurisdictionResolver';

const props = defineProps<{
    item: {
        button?: boolean;
        buttonLabel?: string;
        buttonShortLabel?: string; // 미니 사이드바일 때 표시할 짧은 라벨
        buttonIcon?: string;
        buttonColor?: string;
        buttonAction?: string | (() => void);
        buttonBadge?: string;
        buttonBadgeColor?: string;
    };
}>();

const router = useRouter();
const languageStore = useLanguageStore();
const customizerStore = useCustomizerStore();
const sessionStore = useSessionStore();
const preSetStore = usePreSetStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);

// 사이드바 hover 확장 상태 inject (VerticalSidebar에서 provide)
const sidebarRailExpanded = inject<Ref<boolean>>('sidebarRailExpanded');

// 실제로 축소된 상태인지 (미니 모드이고, hover로 확장되지 않은 상태)
const isCollapsed = computed(() => {
    return customizerStore.mini_sidebar && !sidebarRailExpanded?.value;
});

// 다국어 처리: buttonLabel이 있으면 다국어 키로 변환 시도
const getButtonLabel = () => {
    if (!props.item.buttonLabel) return '';
    // 다국어 키인 경우 (menu. 접두사 사용)
    return t('menu.' + props.item.buttonLabel) || props.item.buttonLabel;
};

const handleClick = () => {
    // 모든 버튼 동작 (Start Task 포함)
    if (typeof props.item.buttonAction === 'string') {
        const targetPath = props.item.buttonAction;
        const currentPath = router.currentRoute.value.path;

        // ★ Start Task (/tasks/new) 인터셉트: PreSet 지원 jurisdiction 은 현재 페이지 위에 모달.
        //   MY = 모든 환경 / SG = local·dev / 그 외 = 라우팅 fallback. (jurisdictionResolver 가 결정)
        if (targetPath === '/tasks/new') {
            if (isPreSetSupported()) {
                preSetStore.openModal();
                return;
            }
        }

        // 같은 라우트로 이동하는 경우 데이터 재조회
        if (currentPath === targetPath) {
            // _refresh 파라미터를 추가하여 컴포넌트 재마운트 및 데이터 재조회
            router.replace({ path: targetPath, query: { _refresh: Date.now() } });
        } else {
            // 다른 경로로 이동하는 경우 query 파라미터 제거
            router.push({ path: targetPath, query: {} });
        }
    } else if (typeof props.item.buttonAction === 'function') {
        props.item.buttonAction();
    }
};
</script>

<template>
    <div class="nav-button-wrapper mb-2 position-relative" :class="{ 'mini-mode': isCollapsed }">
        <!-- 모든 버튼 (메뉴 없이 바로 이동) -->
        <v-btn
            :color="item.buttonColor || 'primary'"
            :block="!isCollapsed"
            rounded="lg"
            :class="['nav-button', { 'nav-button-mini': isCollapsed }]"
            @click="handleClick"
        >
            <!-- 축소 모드 (미니 + hover 아님): 짧은 라벨만 표시 -->
            <template v-if="isCollapsed">
                <span class="mini-label">{{ item.buttonShortLabel || 'S' }}</span>
            </template>
            <!-- 확장 모드 (일반 또는 hover 확장): 아이콘 + 전체 라벨 -->
            <template v-else>
                <Icon
                    v-if="item.buttonIcon"
                    :icon="item.buttonIcon"
                    class="mr-2"
                    size="20"
                />
                <span class="hide-menu">{{ getButtonLabel() }}</span>
            </template>
        </v-btn>
        <v-avatar
            v-if="item.buttonBadge && !isCollapsed"
            :color="item.buttonBadgeColor || 'purple'"
            size="24"
            class="nav-button-badge"
        >
            <span class="text-white text-caption font-weight-bold">{{ item.buttonBadge }}</span>
        </v-avatar>
    </div>
</template>

<style scoped>
.nav-button-wrapper {
    position: relative;
    padding: 0 8px;
    margin-bottom: 8px;
}

/* 미니 모드일 때 중앙 정렬 */
.nav-button-wrapper.mini-mode {
    padding: 0 4px;
    display: flex;
    justify-content: center;
}

.nav-button {
    position: relative;
    z-index: 1;
    height: 40px !important;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
}

/* 미니 모드 버튼 스타일 */
.nav-button-mini {
    min-width: 40px !important;
    width: 40px !important;
    height: 40px !important;
    padding: 0 !important;
    border-radius: 50% !important;
}

.mini-label {
    font-size: 16px;
    font-weight: 600;
}

.nav-button-badge {
    position: absolute;
    top: -6px;
    right: 8px;
    z-index: 2;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    border: 2px solid white;
}

.hide-menu {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>

